import { signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { plainToClass, plainToInstance } from 'class-transformer';
import { Observable, Subject, Subscription } from 'rxjs';

import { Media } from '@eiq/files/models';

import {
  AIPromptCore,
  BlockType,
  AllowedForCopyCardsQuestionTypes,
  AllowedForCopyOptionsQuestionTypes,
  CarouselType,
  ChoiceQuestionTypes,
  Command,
  Condition,
  IFollowUp,
  InputType,
  Operator,
  QuestionType,
  QuestionTypesNoAvailableForConditionallity,
  SurveySettings
} from '@empathy/survey/base';
import { Localization, MediaForm, PageModel, Question, SurveyModel } from '@empathy/survey-core';

import { BlockTypeContainers } from './block-type.constants';
import { IEditorForm } from './editor-form.interface';
import { ILocalizationForm } from './localization-form.model';
import { PForm, PageForm } from './page-form.model';
import { SettingsForm } from './settings-form.model';
import { SurveyBlocks } from './survey-editor-blocks.model';
import { IBlockForm } from '../blocks/block-form.interface';
import { IConditionForm } from '../conditions/condition-form.model';
import { ISourceForm } from '../sources/source-form.model';
import { SurveySources } from '../sources/survey-editor-sources.model';
import { QuestionFormInstanceByType } from '../type-forms/form-type-instance.constant';
import { IQuestionCardForm, IQuestionForm, IQuestionOptionForm } from '../type-forms/question-form-group.interface';
import { QuestionForm } from '../type-forms/question-form.model';

export class SurveyEditor {
  isSaving$ = signal<boolean>(false);
  questionActivated$ = new Subject<void>();

  activeOption: FormGroup<IQuestionOptionForm>;
  activeHiddenOption: FormGroup<IQuestionOptionForm>;
  activeCard: FormGroup<IQuestionCardForm>;
  aiFollowupCall: (prompt: AIPromptCore) => Observable<IFollowUp>;

  settingsForm = new SettingsForm();

  form = new FormGroup<IEditorForm>({
    title: new FormControl(''),
    description: new FormControl(''),
    welcomeHtml: new FormControl(''),
    welcomeStimulus: new FormArray([]),
    useEndPageInd: new FormControl(false),
    endPageHtml: new FormControl(''),
    endButtonText: new FormControl('Close Survey'),
    questions: new FormArray<FormGroup<IQuestionForm>>([]),
    questionsHidden: new FormArray<FormGroup<IQuestionForm>>([]),
    pages: new FormArray<PForm>([]),
    blocks: new FormArray<FormGroup<IBlockForm>>([]),
    sources: new FormArray<FormGroup<ISourceForm>>([]),
    programmerNotes: new FormControl(''),
    settings: this.settingsForm.form,
    loc: new FormArray<FormGroup<ILocalizationForm>>([])
  });

  blocks = new SurveyBlocks(this.form);
  sources = new SurveySources(this.form);

  private subscription: Subscription = null;
  private questionsIdsMap = new Map<string, Question>();
  private questionsHiddenIdsMap = new Map<string, Question>();

  constructor(survey?: string) {
    if (survey) {
      this.JSON = survey;
    }

    this.listenSettingsModerationInd();

    const globalMonitoringInd = this.form.get('settings').get('monitorTerms').get('monitorProblematicInd').value;
    this.setQuestionMonitoringBasedOnGlobalRules(globalMonitoringInd);

    this.questions.forEach((q) => this.questionsIdsMap.set(q.id, q));
    this.questionsHidden.forEach((q) => this.questionsHiddenIdsMap.set(q.id, q));
  }

  /**
   * A survey model JSON schema.
   *
   * This property allows you to get or set the JSON schema of a survey model.
   */
  get JSON(): string {
    return JSON.stringify(this.model);
  }

  get model(): SurveyModel {
    return this.mapToSurveyModel();
  }

  // !!TODO: How to make the JSON properties accessible from outside the library but not the below properties

  get programmerNotes(): string {
    return this.form.get('programmerNotes').value;
  }

  get pages(): Partial<PageModel>[] {
    return this.form.get('pages').value;
  }

  get questions(): Question[] {
    return plainToInstance(Question, this.form.get('questions').value);
  }

  get questionsHidden(): Question[] {
    return plainToInstance(Question, this.form.get('questionsHidden').value);
  }

  get questionsCount(): number {
    return this.questionsCtrls.length;
  }

  get questionsCtrls(): FormArray<FormGroup<IQuestionForm>> {
    return this.form.get('questions') as FormArray<FormGroup<IQuestionForm>>;
  }

  get questionsHiddenCtrls(): FormArray<FormGroup<IQuestionForm>> {
    return this.form.get('questionsHidden') as FormArray<FormGroup<IQuestionForm>>;
  }

  get questionsIdsWithQuestion(): Map<string, Question> {
    return this.questionsIdsMap;
  }

  get questionsIdsWithHiddenQuestion(): Map<string, Question> {
    return this.questionsHiddenIdsMap;
  }

  get activeQuestionIndex(): number {
    return this.form.value.questions.findIndex((q) => q.active === true);
  }

  get activeHiddenQuestionIndex(): number {
    return this.form.value.questionsHidden.findIndex((q) => q.active === true);
  }

  get activeQuestion(): FormGroup {
    if (this.activeQuestionIndex > -1) {
      return <FormGroup>(this.form.get('questions') as FormArray).at(this.activeQuestionIndex);
    }

    return null;
  }

  get activeHiddenQuestion(): FormGroup {
    if (this.activeHiddenQuestionIndex > -1) {
      return <FormGroup>(this.form.get('questionsHidden') as FormArray).at(this.activeHiddenQuestionIndex);
    }

    return null;
  }

  get activeQuestionInformation(): FormControl<string> {
    return this.activeQuestion.get('information') as FormControl;
  }

  get activeQuestionCards(): FormArray<FormGroup<IQuestionOptionForm>> {
    return this.activeQuestion.get('cards') as FormArray;
  }

  get activeQuestionOptions(): FormArray<FormGroup<IQuestionOptionForm>> {
    return this.activeQuestion.get('options') as FormArray;
  }

  get activeQuestionRanks(): FormArray<FormGroup<IQuestionOptionForm>> {
    return this.activeQuestion.get('ranks') as FormArray;
  }

  get activeHiddenQuestionOptions(): FormArray<FormGroup<IQuestionOptionForm>> {
    return this.activeHiddenQuestion.get('options') as FormArray;
  }

  get activeQuestionConditionals(): FormArray<FormGroup<IConditionForm> | FormControl<Command>> {
    return this.activeQuestion.get('visibleWhen') as FormArray<FormGroup<IConditionForm> | FormControl<Command>>;
  }

  get activeQuestionOptionConditionals(): FormArray<FormGroup<IConditionForm> | FormControl<Command>> {
    return this.activeOption.get('visibleWhen') as FormArray<FormGroup<IConditionForm> | FormControl<Command>>;
  }

  get activeHiddenQuestionOptionConditionals(): FormArray<FormGroup<IConditionForm> | FormControl<Command>> {
    return this.activeHiddenOption.get('selectedWhen') as FormArray<FormGroup<IConditionForm> | FormControl<Command>>;
  }

  get activeQuestionCardConditionals(): FormArray<FormGroup<IConditionForm> | FormControl<Command>> {
    return this.activeCard.get('visibleWhen') as FormArray<FormGroup<IConditionForm> | FormControl<Command>>;
  }

  get allAllowedCopyOptionsTypeQuestions(): Question[] {
    return this.questions.filter((q) => AllowedForCopyOptionsQuestionTypes.includes(q.type));
  }

  get allAllowedCopyCardsTypeQuestions(): Question[] {
    return this.questions.filter((q) => AllowedForCopyCardsQuestionTypes.includes(q.type));
  }

  get previousSelectableQuestions(): Question[] {
    return this.questions.slice(0, this.activeQuestionIndex).filter((q) => !QuestionTypesNoAvailableForConditionallity.includes(q.type));
  }

  get previousChoiceSelectableQuestions(): Question[] {
    return this.questions
      .slice(0, this.activeQuestionIndex)
      .filter((q) => !QuestionTypesNoAvailableForConditionallity.includes(q.type) && ChoiceQuestionTypes.includes(q.type) && !CarouselType.includes(q.type));
  }

  get affectedQuestionsOnConditional(): string[] {
    const activeQuestionId = this.activeQuestion.get('id').value;
    return this.affectedQuestionsOnConditionById(activeQuestionId);
  }

  get affectedHiddenQuestionsOnConditional(): string[] {
    const activeQuestionId = this.activeQuestion.get('id').value;
    return this.affectedHiddenQuestionsOnConditionById(activeQuestionId);
  }

  get lastQuestionId(): number {
    const questionIds: number[] = this.questions.map((q) => +q.id.replace(/^\D+/g, ''));
    return this.getLastId(questionIds);
  }

  get lastHiddenQuestionId(): number {
    const hiddenIds: number[] = this.questionsHidden.map((q) => +q.id.replace(/^\D+/g, ''));
    return this.getLastId(hiddenIds);
  }

  set JSON(survey: string) {
    this.patch(JSON.parse(survey));
  }

  set activateQuestion(newActiveQuestionId: string) {
    this.activeOption = null;
    this.activeCard = null;

    if (this.activeQuestionIndex > -1) {
      const currentActiveQuestion = (<FormArray>this.form.get('questions')).at(this.activeQuestionIndex);
      currentActiveQuestion.get('active').setValue(false, { emitEvent: false });
    }

    if (!newActiveQuestionId) {
      return;
    }

    let newActiveQuestion = (this.form.get('questions') as FormArray).controls.find((q: FormGroup) => q.get('id').value === newActiveQuestionId);

    if (!newActiveQuestion) {
      newActiveQuestion = (this.form.get('questions') as FormArray).controls.find((q: FormGroup) => q.get('code').value === newActiveQuestionId);
    }

    newActiveQuestion.get('active').setValue(true, { emitEvent: false });
    this.questionActivated$.next();
  }

  set activateHiddenQuestion(newActiveHiddenQuestionId: string) {
    this.activeHiddenOption = null;

    if (this.activeHiddenQuestionIndex >= 0) {
      const currentActiveQuestion = (<FormArray>this.form.get('questionsHidden')).at(this.activeHiddenQuestionIndex);
      currentActiveQuestion.get('active').setValue(false, { emitEvent: false });
    }

    if (!newActiveHiddenQuestionId) {
      return;
    }

    const newActiveHiddenQuestion = (this.form.get('questionsHidden') as FormArray).controls.find(
      (q: FormGroup) => q.get('id').value === newActiveHiddenQuestionId
    );
    newActiveHiddenQuestion.get('active').setValue(true, { emitEvent: false });
  }

  deactivateActiveQuestion(): void {
    this.activeOption = null;
    this.activeCard = null;

    if (this.activeQuestionIndex > 0) {
      const currentActiveQuestion = (<FormArray>this.form.get('questions')).at(this.activeQuestionIndex);
      currentActiveQuestion.get('active').setValue(false, { emitEvent: false });
    }

    this.questionActivated$.next();
  }

  deactivateActiveHiddenQuestion(): void {
    this.activeHiddenOption = null;

    if (this.activeHiddenQuestionIndex > 0) {
      const currentActiveQuestion = (<FormArray>this.form.get('questionsHidden')).at(this.activeHiddenQuestionIndex);
      currentActiveQuestion.get('active').setValue(false, { emitEvent: false });
    }
  }

  getQuestion(qId: string): Question {
    return this.questions.find((q) => q.id === qId);
  }

  getHiddenQuestion(qId: string): Question {
    return this.questionsHidden.find((q) => q.id === qId);
  }

  getQuestionIndex(qId: string): number {
    return this.questions.findIndex((q) => q.id === qId);
  }

  getHiddenQuestionIndex(qId: string): number {
    return this.questionsHidden.findIndex((q) => q.id === qId);
  }

  pushPage(pageForm: PageForm): number {
    (this.form.get('pages') as FormArray).push(pageForm.form);
    return this.pages.length - 1;
  }

  pushQuestion(activeIndex: number, qForm: QuestionForm): number {
    const newIndex = activeIndex + 1;

    this.addItemsToQuestionIdsMap([qForm.form.value as Question]);

    if (newIndex > this.questions.length - 1) {
      (this.form.get('questions') as FormArray).push(qForm.form);
      return this.questions.length - 1;
    }

    (this.form.get('questions') as FormArray).insert(newIndex, qForm.form);

    return newIndex;
  }

  pushHiddenQuestion(activeIndex: number, qForm: QuestionForm): number {
    const newIndex = activeIndex + 1;

    this.addItemsToHiddenQuestionIdsMap([qForm.form.value as Question]);

    if (newIndex > this.questionsHidden.length - 1) {
      (this.form.get('questionsHidden') as FormArray).push(qForm.form);
      return this.questionsHidden.length - 1;
    }

    (this.form.get('questionsHidden') as FormArray).insert(newIndex, qForm.form);

    return newIndex;
  }

  removeActiveQuestion(): void {
    this.removeQuestion(this.activeQuestionIndex);
  }

  removeActiveHiddenQuestion(): void {
    this.removeHiddenQuestion(this.activeHiddenQuestionIndex);
  }

  removeQuestion(questionIndex: number): void {
    this.questionsIdsMap.delete(this.questions[questionIndex].id);
    (this.form.get('questions') as FormArray).removeAt(questionIndex);
  }

  removeHiddenQuestion(questionHiddenIndex: number): void {
    this.questionsHiddenIdsMap.delete(this.questionsHidden[questionHiddenIndex].id);
    (this.form.get('questionsHidden') as FormArray).removeAt(questionHiddenIndex);
  }

  removePage(pageId: string): void {
    const pageIndex = this.form.get('pages').value.findIndex((p) => p.id === pageId);

    if (pageIndex < 0) {
      return;
    }

    (this.form.get('pages') as FormArray).removeAt(pageIndex);
    const newPageId = (this.form.get('pages') as FormArray).at(pageIndex - 1).get('id').value;

    this.questionsCtrls.controls.forEach((q) => {
      if (q.get('pageId').value === pageId) {
        q.get('pageId').setValue(newPageId);
      }
    });

    this.amendPages();
  }

  affectedQuestionsOnConditionById(questionId: string): string[] {
    const questionsUsingActiveQuestionAsCondition = this.questions.filter(
      (q) =>
        q.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId) ||
        q.options?.some((opt) => opt.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId)) ||
        q.cards?.some((opt) => opt.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId))
    );
    return questionsUsingActiveQuestionAsCondition.map((q) => q.id);
  }

  affectedQuestionsByIncompatibleCondition(questionId: string, allowedOperators: Operator[]): Question[] {
    const isIncompatible = (vw: Condition): boolean => vw.questionId === questionId && !allowedOperators.includes(vw.operator);

    return this.questions.filter(
      (q) =>
        q.visibleWhen.filter((vw) => typeof vw === 'object').some(isIncompatible) ||
        q.options?.some((opt) => opt.visibleWhen.filter((vw) => typeof vw === 'object').some(isIncompatible)) ||
        q.cards?.some((card) => card.visibleWhen.filter((vw) => typeof vw === 'object').some(isIncompatible))
    );
  }

  affectedHiddenQuestionsOnConditionById(questionId: string): string[] {
    const questionsUsingActiveQuestionAsCondition = this.questionsHidden.filter((q) =>
      q.options?.some((opt) => opt.selectedWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId))
    );

    return questionsUsingActiveQuestionAsCondition.map((q) => q.id);
  }

  affectedHiddenQuestionsByIncompatibleCondition(questionId: string, allowedOperators: Operator[]): Question[] {
    const isIncompatible = (vw: Condition): boolean => vw.questionId === questionId && !allowedOperators.includes(vw.operator);

    return this.questionsHidden.filter((q) => q.options?.some((opt) => opt.selectedWhen.filter((vw) => typeof vw === 'object').some(isIncompatible)));
  }

  affectedQuestionsOnConditionByOptionId(questionId: string, optionId: string): string[] {
    const questionsUsingActiveQuestionAsCondition = this.questions.filter(
      (q) =>
        q.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.valueToMatch.includes(optionId)) ||
        q.options?.some((opt) =>
          opt.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.valueToMatch.includes(optionId))
        ) ||
        q.cards?.some((opt) =>
          opt.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.valueToMatch.includes(optionId))
        )
    );
    return questionsUsingActiveQuestionAsCondition.map((q) => q.id);
  }

  affectedHiddenQuestionsOnConditionByHiddenOptionId(questionId: string, optionId: string): string[] {
    const questionsUsingActiveQuestionAsCondition = this.questionsHidden.filter((q) =>
      q.options?.some((opt) =>
        opt.selectedWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.valueToMatch.includes(optionId))
      )
    );
    return questionsUsingActiveQuestionAsCondition.map((q) => q.id);
  }

  affectedQuestionsOnConditionByCardId(questionId: string, cardId: string): string[] {
    const questionsUsingActiveQuestionAsCondition = this.questions.filter(
      (q) =>
        q.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.cardId === cardId) ||
        q.options?.some((opt) =>
          opt.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.cardId === cardId)
        ) ||
        q.cards?.some((opt) =>
          opt.visibleWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.cardId === cardId)
        )
    );
    return questionsUsingActiveQuestionAsCondition.map((q) => q.id);
  }

  affectedHiddenQuestionsOnConditionByCardId(questionId: string, cardId: string): string[] {
    const questionsUsingActiveQuestionAsCondition = this.questionsHidden.filter((q) =>
      q.options?.some((opt) =>
        opt.selectedWhen.filter((vw) => typeof vw === 'object').some((vw: Condition) => vw.questionId === questionId && vw.cardId === cardId)
      )
    );
    return questionsUsingActiveQuestionAsCondition.map((q) => q.id);
  }

  moveQuestion(previousIndex: number, currentIndex: number): string {
    const dir = currentIndex > previousIndex ? 1 : -1;

    const from = previousIndex;
    const to = currentIndex;

    const temp = this.questionsCtrls.at(from);
    for (let i = from; i * dir < to * dir; i = i + dir) {
      const current = this.questionsCtrls.at(i + dir);
      this.questionsCtrls.setControl(i, current);
    }
    this.questionsCtrls.setControl(to, temp);

    return this.questionsCtrls.controls[currentIndex].get('id').value;
  }

  amendPages(): void {
    (this.form.get('pages') as FormArray).clear();

    let currentPage: string = null;
    let pageIndex = 0;
    this.questionsCtrls.controls.forEach((ctrl) => {
      if (ctrl.get('pageId').value !== currentPage) {
        currentPage = ctrl.get('pageId').value;
        pageIndex++;
        const page = new PageForm(`page_${pageIndex}`);
        this.pushPage(page);
      }
      ctrl.get('pageId').setValue(`page_${pageIndex}`);
    });
  }

  previousConditionSelectableQuestionsWithBlockAllowed(): Question[] {
    const loopBlock = this.blocks.byGroupedQId(this.activeQuestion.get('id').value);
    if (loopBlock && loopBlock.type === BlockType.Loop) {
      return [
        ...this.questions
          .slice(0, this.activeQuestionIndex)
          .filter(
            (q) =>
              (!this.blocks.isOnLoopBlock(q.id) || loopBlock.groupedQuestionIds.includes(q.id)) && !QuestionTypesNoAvailableForConditionallity.includes(q.type)
          ),
        ...this.selectableHiddenQuestions()
      ];
    } else {
      return [
        ...this.questions
          .slice(0, this.activeQuestionIndex)
          .filter((q) => !this.blocks.isOnLoopBlock(q.id) && !QuestionTypesNoAvailableForConditionallity.includes(q.type)),
        ...this.selectableHiddenQuestions()
      ];
    }
  }

  previousConditionSelectableQuestionsForBlock(): Question[] {
    const activeBlock = this.blocks.active$().value;
    const questions = this.questions;
    let firstQuestionOnBlockIndex: number;
    if (BlockTypeContainers.includes(activeBlock.type)) {
      firstQuestionOnBlockIndex = this.blocks.parentStartQuestionIndex(activeBlock.groupedBlockIds, questions);
      // A draft loop source can be chosen without a position; parent conditions need a known preceding boundary.
      if (firstQuestionOnBlockIndex === -1) {
        return activeBlock.type === BlockType.LoopParent ? this.selectableHiddenQuestions() : [];
      }
    } else {
      const firstQuestionOnBlock = activeBlock.groupedQuestionIds[0];
      if (!firstQuestionOnBlock) {
        return [];
      }
      firstQuestionOnBlockIndex = questions.findIndex((question) => question.id === firstQuestionOnBlock);
    }

    return [
      ...questions
        .slice(0, firstQuestionOnBlockIndex)
        .filter((question) => !this.blocks.isOnLoopBlock(question.id) && !QuestionTypesNoAvailableForConditionallity.includes(question.type)),
      ...this.selectableHiddenQuestions()
    ];
  }

  previousConditionSelectableQuestionsForHiddenQuestions(): Question[] {
    return [
      ...this.questions.filter((q) => !QuestionTypesNoAvailableForConditionallity.includes(q.type)),
      ...this.selectableHiddenQuestions(this.activeHiddenQuestion?.get('id')?.value)
    ];
  }

  affectedQuestionsOnConditionalOfSelectedQuestion(qId: string): Question[] {
    const questionIds = this.affectedQuestionsOnConditionById(qId);
    const questions = questionIds.map((id) => this.questionsIdsMap.get(id));
    return questions;
  }

  affectedHiddenQuestionsOnConditionalOfSelectedQuestion(qId: string): Question[] {
    const questionIds = this.affectedHiddenQuestionsOnConditionById(qId);
    const questions = questionIds.map((id) => this.questionsHiddenIdsMap.get(id));
    return questions;
  }

  addItemsToQuestionIdsMap(questions: Question[]): void {
    questions.forEach((q) => this.questionsIdsMap.set(q.id, q));
  }

  addItemsToHiddenQuestionIdsMap(questions: Question[]): void {
    questions.forEach((q) => this.questionsHiddenIdsMap.set(q.id, q));
  }

  addLanguage(languageCode: string, emitEvent = true): void {
    (this.form.get('loc') as FormArray<FormGroup<ILocalizationForm>>).push(
      new FormGroup<ILocalizationForm>({
        lc: new FormControl(languageCode)
      }),
      { emitEvent }
    );

    this.sources.addLanguageVariableOption(languageCode);
  }

  removeLanguage(languageCode: string): void {
    const locArray = this.form.get('loc') as FormArray<FormGroup<ILocalizationForm>>;

    const index = locArray.controls.findIndex((group) => group.get('lc')?.value === languageCode);

    if (index === -1) {
      return;
    }

    locArray.removeAt(index);

    this.sources.removeLanguageVariableOption(languageCode);
  }

  private selectableHiddenQuestions(excludedQuestionId?: string): Question[] {
    return this.questionsHidden.filter((q) => q.id !== excludedQuestionId && !QuestionTypesNoAvailableForConditionallity.includes(q.type));
  }

  private patch(survey: SurveyModel): void {
    if (!survey.settings) {
      survey.settings = new SurveySettings();
    }

    this.form.patchValue(survey);
    this.settingsForm.init(survey.settings);
    this.patchStimulus(survey.welcomeStimulus);

    if (!survey.pages) {
      return;
    }

    survey.pages.forEach((page) => {
      const pageForm = new PageForm(page.id);
      (this.form.get('pages') as FormArray).push(pageForm.form);

      page.questions.forEach((q) => {
        const question = QuestionFormInstanceByType(q.type);
        q.pageId = page.id;
        question.init(q);
        (this.form.get('questions') as FormArray).push(question.form);
      });
    });

    survey.questionsHidden?.forEach((q) => {
      const question = QuestionFormInstanceByType(q.type);
      question.init(q as Question);
      (this.form.get('questionsHidden') as FormArray).push(question.form);
    });

    survey.loc?.forEach((l) => {
      (this.form.get('loc') as FormArray<FormGroup<ILocalizationForm>>).push(
        new FormGroup<ILocalizationForm>({
          lc: new FormControl(l.lc)
        })
      );
    });

    this.blocks.patch(survey.blocks);
    this.sources.patch(survey.sources);
  }

  private patchStimulus(stimulus: Media[]): void {
    if (!stimulus || stimulus.length === 0) {
      return;
    }

    stimulus.forEach((s) => {
      (this.form.get('welcomeStimulus') as FormArray).push(new MediaForm(s).form);
    });
  }

  private mapToSurveyModel(): SurveyModel {
    const formValues = this.form.value;
    return plainToClass(SurveyModel, {
      title: formValues.title,
      description: formValues.description,
      welcomeHtml: formValues.welcomeHtml,
      welcomeStimulus: formValues.welcomeStimulus.map((stimulus) => plainToClass(Media, stimulus)),
      useEndPageInd: formValues.useEndPageInd,
      endPageHtml: formValues.endPageHtml,
      endButtonText: formValues.endButtonText,
      pages: this.mapPagesArray(),
      questionsHidden: this.mapHiddenQuestionsArray(),
      blocks: this.blocks.map(),
      sources: this.sources.map(),
      settings: plainToClass(SurveySettings, formValues.settings),
      loc: formValues.loc.map((loc) => plainToClass(Localization, loc))
    });
  }

  private mapPagesArray(): PageModel[] {
    return this.form.value.pages.map((p) => {
      const page = new PageModel();
      page.id = p.id;
      page.questions = this.form.value.questions.filter((q) => q.pageId === p.id).map((question) => this.mapQuestion(question as Question));
      return page;
    });
  }

  private mapHiddenQuestionsArray(): Question[] {
    return this.form.value.questionsHidden.map((question) => this.mapQuestion(question as Question));
  }

  private mapQuestion(question: Question): Question {
    const mapped = plainToClass(Question, question);
    if (mapped.type === QuestionType.Input && mapped.inputType !== InputType.Text) {
      mapped.monitorTerms = undefined;
    }
    if (mapped.type === QuestionType.AIChat && mapped.discussionGuide) {
      mapped.discussionGuide = mapped.discussionGuide.trim();
    }
    return mapped;
  }

  private listenSettingsModerationInd(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.form
      .get('settings')
      .get('monitorTerms')
      .get('monitorProblematicInd')
      .valueChanges.subscribe((value) => {
        this.setQuestionMonitoringBasedOnGlobalRules(value);
      });
  }

  private setQuestionMonitoringBasedOnGlobalRules(value: boolean): void {
    (this.form.get('questions') as FormArray<FormGroup<IQuestionForm>>).controls.forEach((q) => {
      if (value && q.get('monitorTerms')) {
        q.get('monitorTerms').get('monitorProblematicInd').setValue(true);
      }
    });
  }

  private getLastId(ids: number[]): number {
    return ids.length === 0 ? 0 : Math.max.apply(null, ids);
  }
}
